import { ReactNode, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  BiChevronsRight,
  BiChevronsLeft,
  BiChevronDown,
  BiChevronUp,
  BiLogOut,
} from "react-icons/bi";
import { BsFillHouseFill } from "react-icons/bs";
import Logo from "../../../assets/images/logo_bg.png";
import { SCRIPTS_PATH } from "~/services/endpoints";
import { LABELS } from "~/resources/Labels";
import { buildLogoutUrl } from "~/services/keycloak";

interface MenuProps {
  children?: ReactNode;
}

export const SIMPLMenuHeader = ({ children }: MenuProps) => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isVmSubMenuOpen, setIsVmSubMenuOpen] = useState(false);
  const roles: string[] = JSON.parse(sessionStorage.getItem("roles") || "[]");
  const getNavLinkClass = (isActive: boolean) => {
    const baseStyles =
      "text-sm font-semibold text-white flex items-center px-4 py-2 rounded-md transition-all border w-full";
    const activeStyles =
      "bg-opacity-10 bg-[#51E7F9] border-[#51E7F9] border-[1px] text-[#51E7F9]";
    const inactiveStyles = "border-transparent text-white";
    const hoverStyles =
      "hover:border-[#51E7F9] hover:bg-opacity-10 hover:bg-[#51E7F9] hover:border-[1px] hover:text-[#51E7F9]";

    return `${baseStyles} ${isActive ? activeStyles : inactiveStyles} ${hoverStyles}`;
  };

  const vmTemplateSubItems = [
    {
      to: "/vm-template/configuration",
      label: "VM Configuration",
    },
    {
      to: "/vm-template",
      label: "Management",
      roles: ["INFRA_ADMIN"], // ONLY THIS IS RESTRICTED
    },
    {
      to: "/vm-template/post-provisioning",
      label: "Post Provisioning",
    },
    {
      to: "/vm-template/policies",
      label: "Policies",
    },
  ];

  /**
   * Logs the user out by clearing session storage and redirecting to the logout URL.
   *
   * This function retrieves the ID token from sessionStorage, constructs the logout URL
   * using `buildLogoutUrl`, clears the session, and redirects the browser to the logout endpoint.
   * If no ID token is found, it will redirect to the application's home page.
   *
   * @returns {void}
   */
  const handleLogout = (): void => {
    const idToken = sessionStorage.getItem("id_token");
    const redirectUri = globalThis.location.origin;

    if (!idToken) {
      console.warn("No ID token found. Redirecting to home.");
      globalThis.location.href = redirectUri;
      return;
    }

    const logoutUrl = buildLogoutUrl(redirectUri, idToken);

    sessionStorage.clear();
    globalThis.location.href = logoutUrl;
  };

  return (
    // Outer container for the whole screen
    <div className="flex flex-col h-screen relative bg-[#002B6E]">
      {/*  Top Header Bar (Full Width - Matches the image) */}
      <div className="h-34 flex justify-between items-center text-white px-2 sm:px-4 md:px-6 pl-0 sm:pl-0 md:pl-0 h-[72px] relative z-20 bg-[#002B6E] border-b border-gray-700/50">
        <div className="flex items-center">
          <img src={Logo} alt="Logo" className="h-20 w-full mr-4" />
        </div>

        {/* Log Lout*/}
        <div className="flex items-center cursor-pointer text-sm font-medium">
          {/* Log Out Button on the right */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white hover:text-[#51E7F9] transition-colors duration-200 
                     px-3 py-1 rounded-md hover:bg-white hover:bg-opacity-10 text-sm font-medium"
            title="Log out"
          >
            <BiLogOut size={30} />
            Log Out
            <span className="hidden sm:inline">Log Out</span>
          </button>
        </div>
      </div>

      {/* Main Content Wrapper (Contains Sidebar and Page Content) */}
      <div className="flex flex-1 relative">
        {/*Sidebar (Vertical Menu) */}
        <div
          className={`bg-[#002B6E] ${
            isSidebarExpanded
              ? "w-full md:w-[260px]" // increased width
              : "w-full md:w-[60px] md:rounded-tr-[20px] md:rounded-br-[20px]"
          } 
          p-3 md:p-5 text-white transition-all duration-300 flex flex-col overflow-hidden relative z-10`}
        >
          {/* Sidebar Toggle and Menu Text */}
          <div className="flex items-center justify-between mb-8 mt-2">
            <span
              className={`${
                isSidebarExpanded ? "inline-flex items-center" : "hidden"
              } text-sm font-semibold`}
            >
              <BsFillHouseFill size={22} className="mr-1" />
              Menu
            </span>

            <button
              onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
              className={`${
                isSidebarExpanded ? "ml-auto" : "mx-auto"
              } w-6 h-6 rounded-md border-[1px] border-[#51E7F9] 
              p-4 flex items-center justify-center transition-all duration-300`}
            >
              {isSidebarExpanded ? (
                <span className="text-sm font-semibold text-[#51E7F9]">
                  <BiChevronsLeft size={14} />
                </span>
              ) : (
                <span className="text-sm font-semibold text-[#51E7F9]">
                  <BiChevronsRight size={14} />
                </span>
              )}
            </button>
          </div>

          <nav className="flex-1">
            <ul className="space-y-3">
              {/* Deployment Script */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to={SCRIPTS_PATH}
                  // Added styling for collapsed state: show only icon
                  className={({ isActive }) =>
                    isSidebarExpanded
                      ? getNavLinkClass(isActive)
                      : "text-white p-2 rounded-md hover:text-[#51E7F9]"
                  }
                  title={
                    isSidebarExpanded ? undefined : LABELS.DeploymentScript
                  }
                >
                  <span
                    className={`${
                      isSidebarExpanded ? "inline" : "hidden"
                    } text-[14px] leading-[16px] align-middle flex-1`}
                  >
                    {LABELS.DeploymentScript}
                  </span>
                </NavLink>
              </li>

              {/* Contract */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to="/contracts"
                  // Added styling for collapsed state
                  className={({ isActive }) =>
                    isSidebarExpanded
                      ? getNavLinkClass(isActive)
                      : "text-white p-2 rounded-md hover:text-[#51E7F9]"
                  }
                  title={isSidebarExpanded ? undefined : LABELS.Contract}
                >
                  <span
                    className={`${
                      isSidebarExpanded ? "inline" : "hidden"
                    } text-[14px] leading-[16px] align-middle flex-1`}
                  >
                    {LABELS.Contract}
                  </span>
                </NavLink>
              </li>
              {/* Cloud Provisioner Template */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to="/cloud-provisioner-template"
                  className={({ isActive }) =>
                    isSidebarExpanded
                      ? getNavLinkClass(isActive)
                      : "text-white p-2 rounded-md hover:text-[#51E7F9]"
                  }
                  title={isSidebarExpanded ? undefined : LABELS.CloudEnviroment}
                >
                  <span
                    className={`text-[14px] leading-[16px] flex-1 whitespace-nowrap ${
                      isSidebarExpanded ? "inline" : "hidden"
                    } text-[14px] leading-[16px] align-middle flex-1`}
                  >
                    Cloud Provisioner Template
                  </span>
                </NavLink>
              </li>
              {/* Cloud Environment */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to="/cloud-environment"
                  // Added styling for collapsed state
                  className={({ isActive }) =>
                    isSidebarExpanded
                      ? getNavLinkClass(isActive)
                      : "text-white p-2 rounded-md hover:text-[#51E7F9]"
                  }
                  title={isSidebarExpanded ? undefined : LABELS.CloudEnviroment}
                >
                  <span
                    className={`${
                      isSidebarExpanded ? "inline" : "hidden"
                    } text-[14px] leading-[16px] align-middle flex-1`}
                  >
                    {LABELS.CloudEnviroment}
                  </span>
                </NavLink>
              </li>

              {/* VM Template + Submenu (Only visible when expanded) */}
              <li className="mb-2">
                <div
                  className={`flex items-center justify-between px-4 py-2 rounded-md transition-all border 
                    ${isSidebarExpanded ? "w-full" : "hidden"} 
                    border-transparent text-white hover:border-[#51E7F9] hover:bg-opacity-10 hover:bg-[#51E7F9] hover:border-[1px] hover:text-[#51E7F9]`}
                >
                  <nav className="text-sm font-semibold flex items-center flex-1">
                    <span className="text-[14px] leading-[16px] flex-1">
                      {LABELS.VMTemplate}
                    </span>
                  </nav>
                  <button
                    onClick={() => setIsVmSubMenuOpen(!isVmSubMenuOpen)}
                    className="ml-2"
                  >
                    {isVmSubMenuOpen ? (
                      <BiChevronUp size={18} className="text-[#51E7F9]" />
                    ) : (
                      <BiChevronDown size={18} className="text-[#51E7F9]" />
                    )}
                  </button>
                </div>

                {isVmSubMenuOpen && isSidebarExpanded && (
                  <ul className="ml-6 mt-2 space-y-2">
                    {vmTemplateSubItems.map(({ to, label }) => (
                      <li key={to}>
                        <NavLink
                          to={to}
                          className={({ isActive }) =>
                            getNavLinkClass(isActive)
                          }
                        >
                          <span>{label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </ul>
          </nav>
        </div>

        {/* Page Content (The main white area with the rounded corner) */}
        <div className="p-4 md:p-6 flex-1 overflow-auto bg-white rounded-tl-[8px] shadow-inner">
          {Array.isArray(children)
            ? children.map((child) => <div key={child}>{child}</div>)
            : children}
        </div>
      </div>
    </div>
  );
};
